import React, { FormEvent, useEffect, useMemo, useState } from "react";
import { createRoot } from "react-dom/client";
import { analytics } from "./services/analytics";
import { getAttributionSnapshot } from "./services/attribution";
import "./main.css";

type PlanKey = "pro" | "lifetime";
type NicheIconKey = "barber" | "salon" | "pet" | "restaurant" | "clinic" | "repair" | "fight" | "service";

interface Offer {
  key: PlanKey;
  name: string;
  tag: string;
  price: string;
  period: string;
  description: string;
  cta: string;
  featuredLabel: string;
  features: string[];
}

interface CheckoutForm {
  plan: PlanKey;
  name: string;
  email: string;
  whatsapp: string;
  businessName: string;
  segment: string;
  city: string;
  notes: string;
  hasDomain: string;
  hasLogo: string;
  slogan: string;
  desiredDomain: string;
  preferredColors: string;
  preferredStyle: string;
}

interface DiagnosticForm {
  name: string;
  email: string;
  whatsapp: string;
  businessName: string;
  city: string;
  segment: string;
  instagram: string;
  googleMapsUrl: string;
  currentWebsite: string;
  mainGoal: string;
  notes: string;
  consent: boolean;
}

interface DiagnosticSnapshot extends Omit<DiagnosticForm, "consent"> {
  leadId: string;
  createdAt: string;
}

interface DiagnosticResultBlock {
  name: string;
  desc: string;
  value: number;
  color: string;
}

interface DiagnosticResultSource {
  label: string;
  value: string;
  status: "ok" | "skipped" | "failed";
}

interface DiagnosticResultModel {
  date: string;
  businessName: string;
  segment: string;
  location: string;
  score: number;
  tone: "good" | "medium" | "low";
  ratingLabel: string;
  summary: string;
  blocks: DiagnosticResultBlock[];
  strengths: string[];
  attentions: string[];
  recommendations: string[];
  commercialTitle: string;
  commercialText: string;
  sources: DiagnosticResultSource[];
  isLive: boolean;
}

interface DiagnosticApiPayload {
  ok?: boolean;
  diagnostic?: {
    score?: number;
    ratingLabel?: string;
    summary?: string;
    commercialTitle?: string;
    commercialText?: string;
    strengths?: unknown[];
    attentions?: unknown[];
    recommendations?: unknown[];
    blocks?: unknown[];
    sources?: Record<string, unknown>;
    createdAt?: string;
    updatedAt?: string;
  };
  error?: string;
}

const offers: Record<PlanKey, Offer> = {
  pro: {
    key: "pro",
    name: "Pro",
    tag: "Gerenciado",
    price: "R$197",
    period: "/mês",
    description:
      "Site gerenciado com domínio próprio, hospedagem, suporte, pequenos ajustes mensais e relatório. Alguém cuidando do seu site sempre.",
    cta: "Assinar plano Pro",
    featuredLabel: "Mais completo",
    features: [
      "Site institucional de até 5 páginas",
      "Domínio próprio + hospedagem + SSL",
      "WhatsApp, mapa e formulário",
      "SEO técnico e SEO local",
      "Analytics configurado",
      "Relatório mensal por e-mail",
      "Até 3 pequenos ajustes por mês",
      "Suporte em horário comercial"
    ]
  },
  lifetime: {
    key: "lifetime",
    name: "Lifetime",
    tag: "Pagamento único",
    price: "R$1.970",
    period: "uma vez",
    description:
      "Site pronto, publicado em subdomínio gratuito da plataforma, com WhatsApp, mapa, SEO básico e 30 dias de suporte pós-publicação.",
    cta: "Comprar Lifetime",
    featuredLabel: "Sem mensalidade",
    features: [
      "Site institucional de até 5 páginas",
      "Publicado em subdomínio gratuito",
      "Hospedagem + SSL inclusos",
      "WhatsApp, mapa e formulário",
      "SEO técnico e SEO local",
      "Analytics configurado",
      "Relatório mensal por e-mail",
      "30 dias de suporte pós-publicação"
    ]
  }
};

const problems = [
  "Informações importantes ficam perdidas entre stories, posts e mensagens.",
  "O cliente não acha serviços, horário, endereço ou WhatsApp com clareza.",
  "O negócio parece menor ou menos confiável do que realmente é.",
  "Você depende só de Instagram, indicação ou sorte para ser encontrado."
];

const solutions = [
  ["Site profissional", "Layout moderno, responsivo e adaptado ao seu tipo de negócio."],
  ["WhatsApp em destaque", "Botões de contato visíveis e pensados para gerar conversa."],
  ["Mapa e endereço", "Rota, horário e localização organizados para o cliente chegar até você."],
  ["SEO local", "Estrutura técnica para quem procura serviços na sua cidade ou bairro."],
  ["Analytics", "Medição de acessos, cliques e ações importantes em relatórios mensais."],
  ["Sem complicação", "Você conta sobre seu negócio e nós fazemos todo o resto."],
  ["Site em 48 horas", "Receba a primeira versão do site pronto em até 48h após o briefing completo."],
  ["Suporte Humano", "Atendimento com pessoas reais para orientar, revisar e acompanhar seu site."]
];

const steps = [
  ["1", "Você escolhe o plano", "Assinatura mensal ou pagamento único."],
  ["2", "Responde o briefing", "Serviços, endereço, WhatsApp, imagens, cores e preferências."],
  ["3", "Geramos a primeira versão", "Organizamos copy, layout, SEO e estrutura das páginas."],
  ["4", "Você revisa e ajusta", "Pede alterações dentro do limite do plano escolhido."],
  ["5", "Publicamos o site", "No seu domínio próprio ou no subdomínio da plataforma."],
  ["6", "Acompanhamos", "Nós cuidamos de tudo e você recebe relatórios mensais."]
];

const forWho = {
  good: [
    "Seu negócio recebe clientes por WhatsApp, rota, reserva ou orçamento.",
    "Você quer parecer mais profissional quando alguém pesquisa sua empresa.",
    "Você não quer perder tempo tentando montar site sozinho.",
    "Você precisa de uma página clara para enviar a clientes, parceiros e indicações.",
    "Você quer publicar rápido, com orientação humana e sem complicação técnica."
  ],
  bad: [
    "Você precisa de e-commerce completo.",
    "Quer editar o site sozinho todos os dias.",
    "Procura alterações ilimitadas sem custo adicional.",
    "Quer garantia de primeiro lugar no Google.",
    "Precisa de sistema sob medida, área logada ou funcionalidades complexas."
  ]
};

const testimonials = [
  {
    text: "Atendimento sensacional! Meu site ficou pronto super rápido e passou muito mais credibilidade para os meus clientes. Recomendo demais.",
    author: "João Carlos",
    business: "Navalha JC"
  },
  {
    text: "Tinha pressa para colocar minha clínica no ar e me surpreendi com a agilidade. Em 48h estava tudo rodando perfeitamente e lindo.",
    author: "Mariana Costa",
    business: "Clínica Estética Costa"
  },
  {
    text: "Não entendo muito de tecnologia, mas a equipe teve paciência e me ajudou em todos os passos. O layout ficou super moderno.",
    author: "Carlos Mendes",
    business: "Restaurante Sabor Peruano"
  },
  {
    text: "O suporte humano fez toda a diferença. O processo foi muito simples: preenchi o briefing e eles cuidaram do resto. Nota 10!",
    author: "Fernanda Lima",
    business: "Pet Shop Cão Feliz"
  }
];

const niches: Array<{ title: string; text: string; gradient: string; icon: NicheIconKey }> = [
  {
    title: "Barbearia",
    text: "Cortes, barba, pacotes, equipe e agendamento pelo WhatsApp.",
    gradient: "linear-gradient(135deg,#1e3a34,#345a4e)",
    icon: "barber"
  },
  {
    title: "Salão de beleza",
    text: "Serviços, portfólio, valores, equipe e reserva de horário.",
    gradient: "linear-gradient(135deg,#3a2a44,#5c4470)",
    icon: "salon"
  },
  {
    title: "Pet shop",
    text: "Banho, tosa, produtos, diferenciais e localização.",
    gradient: "linear-gradient(135deg,#1f3a44,#356070)",
    icon: "pet"
  },
  {
    title: "Bar e restaurante",
    text: "Cardápio, ambiente, reservas, delivery e rota.",
    gradient: "linear-gradient(135deg,#44321e,#6e5230)",
    icon: "restaurant"
  },
  {
    title: "Clínica estética",
    text: "Procedimentos, especialistas, resultados e agendamento.",
    gradient: "linear-gradient(135deg,#2a3a44,#456070)",
    icon: "clinic"
  },
  {
    title: "Oficina",
    text: "Serviços, especialidades, garantia e orçamento rápido.",
    gradient: "linear-gradient(135deg,#3a3630,#5e574c)",
    icon: "repair"
  },
  {
    title: "Escola de luta",
    text: "Modalidades, professores, horários, planos e matrícula.",
    gradient: "linear-gradient(135deg,#3a1e24,#6e3038)",
    icon: "fight"
  },
  {
    title: "Prestador de serviço",
    text: "Área de atendimento, orçamento, portfólio e avaliações.",
    gradient: "linear-gradient(135deg,#1e3a34,#345a4e)",
    icon: "service"
  }
];

const included = [
  "Site responsivo",
  "Até 5 páginas",
  "Botão de WhatsApp",
  "Formulário de contato",
  "Mapa e endereço",
  "SEO técnico",
  "SEO local",
  "Performance otimizada",
  "Hospedagem e SSL",
  "Analytics configurado",
  "Copy comercial",
  "Publicação assistida"
];

const faqs = [
  [
    "Preciso saber programar?",
    "Não. Você só responde o briefing, envia informações do negócio e aprova a primeira versão. A parte técnica fica com a Vitrine Website."
  ],
  [
    "Posso usar meu domínio próprio?",
    "Sim. Se o cliente já tiver um domínio próprio, nós ajudamos a configurar para apontar para o seu site na Vitrine Website. O domínio será de responsabilidade do cliente e deverá ser pago à parte pelo próprio diretamente ao gerenciador de domínios. Caso opte em deixar tudo com a gente, oferecemos um subdomínio gratuito como meunegocio.vitrine.website em todos os planos. No plano Pro o cliente pode escolher um dominio .com ou .com.br gratuitamente."
  ],
  [
    "Vocês garantem primeiro lugar no Google?",
    "Entregamos estrutura técnica, performance, conteúdo organizado e boas práticas de SEO local validada e comprovada. Mas não garantir posição no Google, nenhum serviço sério deve prometer primeira posição, pois isso depende de fatores externos e voláteis."
  ],
  [
    "Qual é a diferença entre Pro e Lifetime?",
    "O Pro é mensal e inclui acompanhamento e suporte durante todo o período, domínio personalizado gratuito, com possibilidade de ajustes recorrentes. O Lifetime é pagamento único para colocar um site no ar sem mensalidade, o site será em um subbdominio como meunegocio.vitrine.website e suporte de 30 dias após a publicação."
  ],
  [
    "O que acontece depois do pagamento?",
    "Vamos revisar cuidadosamente seu briefing (informações enviadas no formulário) e criaremos a primeira versão do seu site, em seguida um especialista entrará em contato para apresentar, captar mais detalhes e refinar até que você dê sua aprovação para publicar."
  ],
  [
    "Como funciona a garantia?",
    "Oferecemos garantia de satisfação em até 7 dias após o pagamento. Se após apresentação da primeira versão e refinadomento, você ainda não gostar do resultado, nós devolvemos o seu dinheiro sem complicações."
  ],
  [
    "Como funciona o cancelamento?",
    "Você pode cancelar o plano Pro a qualquer momento. Se você cancelar a assinatura do plano Pro, o site será removido do ar em até 30 dias após o cancelamento."
  ],
  [
    "Posso ter acesso ao código ou alterar minha hospedagem?",
    "Nós cuidamos de toda infraestrutura para que você tenha o máximo de performance e segurança. Mas os desenvolvimentos são de propriedade da Vitrine Website e não transferimos a posse do código fonte. O cliente também não poderá utilizar a estrutura, layouts ou códigos para outros fins."
  ],
  [
    "Os sites são otimizados para dispositivos móveis?",
    "Sim. Todos os sites são desenvolvidos com tecnologia responsiva, o que significa que eles se adaptam automaticamente a diferentes tamanhos de tela, incluindo smartphones, tablets e desktops."
  ]
];

const diagnosticProblems = [
  ["Informações espalhadas", "Serviços, horários, endereço e WhatsApp ficam perdidos entre posts, destaques e mensagens."],
  ["Instagram fazendo papel de site", "O perfil mostra o dia a dia, mas nem sempre organiza a decisão de compra."],
  ["Google incompleto", "O cliente encontra o negócio, mas não entende bem o que você oferece ou como entrar em contato."],
  ["Pouca clareza para converter", "A pessoa se interessa, mas não sabe o próximo passo: chamar, reservar, pedir rota ou orçamento."]
];

const diagnosticAnalyzed = [
  ["Google e Maps", "Nome, categoria, endereço, horário, telefone, fotos, site e consistência das informações."],
  ["Instagram", "Bio, link, destaques, prova visual, clareza de serviços e caminho para contato."],
  ["WhatsApp e contato", "Se o caminho até a conversa é simples, direto e fácil de entender."],
  ["Site ou link atual", "Se já existe site: aparência mobile, CTA, mapa, serviços e informações principais."],
  ["Confiança online", "Fotos, avaliações, organização, profissionalismo e coerência entre canais."],
  ["Oportunidades rápidas", "Melhorias práticas para deixar a presença mais clara e pronta para receber clientes."]
];

const diagnosticDeliverables = [
  "Visão geral da presença digital atual",
  "Principais pontos fortes",
  "Problemas que podem gerar desconfiança",
  "Oportunidades no Google, Instagram, site e WhatsApp",
  "Sugestão de estrutura ideal para uma página profissional",
  "Recomendação de próximos passos"
];

const diagnosticSteps = [
  ["1", "Você envia seus dados", "Nome, WhatsApp, segmento, cidade e links do Instagram, Google Maps ou site atual."],
  ["2", "Analisamos sua presença", "Avaliamos as informações públicas e os links enviados com foco em clareza, confiança e contato."],
  ["3", "Você recebe recomendações", "Mostramos pontos fortes, pontos de atenção e próximos passos práticos."]
];

const diagnosticSegments = [
  "Barbearia",
  "Salão de beleza",
  "Clínica estética",
  "Pet shop",
  "Oficina",
  "Escola de luta",
  "Restaurante / Bar",
  "Consultório",
  "Prestador de serviço",
  "Outro"
];

const diagnosticGoals = [
  "Receber mais contatos pelo WhatsApp",
  "Melhorar aparência profissional",
  "Organizar serviços e informações",
  "Aparecer melhor para clientes locais",
  "Melhorar Google / Maps",
  "Entender se preciso de um site",
  "Outro"
];

function useAnalyticsBootstrap() {
  useEffect(() => {
    analytics.init();
    analytics.initClickDelegation();
    analytics.trackPageView(window.location.pathname, document.title);
    // Runs once per full page load; this is a multi-page-style SPA (each
    // route is its own HTML entry point via vite.config.ts), so there is no
    // client-side route change to track beyond this initial mount.
  }, []);
}

function App() {
  useAnalyticsBootstrap();

  const route = window.location.pathname;
  const initialVariant: PlanKey = route.startsWith("/lifetime") ? "lifetime" : "pro";
  const [selectedPlan, setSelectedPlan] = useState<PlanKey>(initialVariant);
  const [openFaq, setOpenFaq] = useState(0);
  const [form, setForm] = useState<CheckoutForm>({
    plan: initialVariant,
    name: "",
    email: "",
    whatsapp: "",
    businessName: "",
    segment: "",
    city: "",
    notes: "",
    hasDomain: "Não",
    hasLogo: "Não",
    slogan: "",
    desiredDomain: "",
    preferredColors: "",
    preferredStyle: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const focus = offers[initialVariant];
  const page = useMemo(() => variantCopy(initialVariant), [initialVariant]);

  if (route.startsWith("/diagnostico/resultado")) return <DiagnosticResultPage />;
  if (route.startsWith("/diagnostico")) return <DiagnosticLanding />;
  if (route.startsWith("/politicas")) return <PoliciesPage />;
  if (route.startsWith("/success")) return <CheckoutResult kind="success" />;
  if (route.startsWith("/cancel")) return <CheckoutResult kind="cancel" />;

  function choosePlan(plan: PlanKey) {
    setSelectedPlan(plan);
    setForm((current) => ({ ...current, plan }));
    analytics.trackProductEvent("select_plan", { plan });
    document.querySelector("#checkout")?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  async function submitCheckout(event: FormEvent) {
    event.preventDefault();
    setSubmitting(true);
    setError("");

    analytics.trackProductEvent("begin_checkout", { plan: selectedPlan });

    try {
      const response = await fetch("/api/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          plan: selectedPlan,
          sourcePath: window.location.pathname,
          project: "vitrine-website",
          gaClientId: analytics.getClientId(),
          attribution: getAttributionSnapshot()
        })
      });
      const payload = (await response.json().catch(() => ({}))) as { url?: string; error?: string };
      if (!response.ok || !payload.url) throw new Error(payload.error || "Não foi possível iniciar o checkout.");
      window.location.href = payload.url;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro ao iniciar o checkout.");
      setSubmitting(false);
    }
  }

  return (
    <div className="site">
      <Header focus={focus} onCta={() => choosePlan(initialVariant)} />

      <main>
        <section className="hero" id="topo">
          <div className="container hero-grid">
            <div>
              <span className="eyebrow">{page.badge}</span>
              <h1>{page.title}</h1>
              <p className="hero-copy">{page.subtitle}</p>
              <div className="hero-actions">
                <button className="button button-primary" onClick={() => choosePlan(initialVariant)}>
                  {focus.cta}
                </button>
                <a className="button button-secondary" href="#planos">
                  Comparar planos
                </a>
              </div>
              <div className="chips">
                {page.chips.map((chip) => (
                  <span className="chip" key={chip}>
                    {chip}
                  </span>
                ))}
              </div>
            </div>

            <SiteMockup />
          </div>
        </section>

        <section className="section section-dark" id="problema">
          <div className="container">
            <div className="section-head">
              <span className="section-label">O problema</span>
              <h2 className="section-title">Seu negócio pode ser ótimo, mas precisa parecer confiável online.</h2>
              <p className="section-copy">
                O cliente pesquisa antes de chamar no WhatsApp, pedir rota ou fazer uma reserva. Se encontra só um Instagram bagunçado, a confiança cai antes da primeira conversa.
              </p>
            </div>
            <div className="problem-grid">
              {problems.map((problem) => (
                <div className="problem" key={problem}>
                  {problem}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="section" id="solucao">
          <div className="container">
            <div className="section-head center">
              <span className="section-label">A solução</span>
              <h2 className="section-title">Uma vitrine digital pronta para receber clientes.</h2>
              <p className="section-copy">
                Criamos o site do seu negócio com as informações certas, visual profissional, WhatsApp, mapa, SEO local e acompanhamento.
              </p>
            </div>
            <div className="solution-grid">
              {solutions.map(([title, text], index) => (
                <div className="solution-card" key={title}>
                  <span className="icon-box">{index + 1}</span>
                  <h3>{title}</h3>
                  <p>{text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="section section-cream" id="como">
          <div className="container">
            <div className="section-head">
              <span className="section-label">Como funciona</span>
              <h2 className="section-title">Do briefing ao site publicado em poucos passos.</h2>
            </div>
            <div className="steps-grid">
              {steps.map(([num, title, text]) => (
                <div className="step" key={num}>
                  <span className="step-num">{num}</span>
                  <h3>{title}</h3>
                  <p>{text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="section" id="para-quem">
          <div className="container">
            <div className="section-head center">
              <span className="section-label">Perfil</span>
              <h2 className="section-title">Para quem a Vitrine Website é ideal?</h2>
            </div>
            <div className="forwho-grid">
              <div className="forwho-card">
                <h3 style={{ color: "var(--green-900)" }}>É para você se:</h3>
                <ul className="forwho-list">
                  {forWho.good.map((item, i) => (
                    <li key={i}>
                      <CheckIcon />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="forwho-card">
                <h3 style={{ color: "var(--danger)" }}>Não é para você se:</h3>
                <ul className="forwho-list">
                  {forWho.bad.map((item, i) => (
                    <li key={i}>
                      <CloseIcon />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section className="section section-dark" id="depoimentos">
          <div className="container">
            <div className="section-head">
              <span className="section-label">Experiência comprovada</span>
              <h2 className="section-title">Conte com a experiência acumulada de 15 anos e mais de 2.000 entregas digitais.</h2>
              <p className="section-copy">
                Veja o que donos de negócios como o seu dizem sobre o nosso trabalho e atendimento.
              </p>
            </div>
            <div className="testimonials-grid">
              {testimonials.map((t, i) => (
                <div className="testimonial-card" key={i}>
                  <p className="testimonial-text">"{t.text}"</p>
                  <div className="testimonial-author">
                    <div className="testimonial-avatar">{t.author.charAt(0)}</div>
                    <div className="testimonial-info">
                      <h4>{t.author}</h4>
                      <span>{t.business}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="section" id="planos">
          <div className="container">
            <div className="section-head center">
              <span className="section-label">Planos</span>
              <h2 className="section-title">{page.planTitle}</h2>
              <p className="section-copy">{page.planSubtitle}</p>
            </div>
            <div className="plans-grid">
              {(["lifetime", "pro"] as PlanKey[]).map((plan) => (
                <PlanCard
                  key={plan}
                  offer={offers[plan]}
                  featured={plan === initialVariant}
                  onChoose={() => choosePlan(plan)}
                />
              ))}
            </div>
            <p className="fineprint">
              Preços em reais. Pagamento seguro processado pela Stripe. Regras de domínio, hospedagem, cancelamento, suporte e ajustes ficam descritas nos termos comerciais.
            </p>
          </div>
        </section>

        <section className="section section-dark" id="exemplos">
          <div className="container">
            <div className="section-head">
              <span className="section-label">Exemplos por segmento</span>
              <h2 className="section-title">Feito para o seu tipo de negócio.</h2>
              <p className="section-copy">
                Cada segmento tem seções e chamadas pensadas para o que os clientes procuram antes de chamar no WhatsApp.
              </p>
            </div>
            <div className="niche-grid">
              {niches.map((niche) => (
                <article className="niche-card" key={niche.title}>
                  <div className="niche-visual" style={{ background: niche.gradient }}>
                    <span className="niche-icon-box">
                      <NicheIcon kind={niche.icon} />
                    </span>
                  </div>
                  <div className="niche-body">
                    <h3>{niche.title}</h3>
                    <p>{niche.text}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="section section-cream" id="incluido">
          <div className="container">
            <div className="section-head">
              <span className="section-label">O que está incluído</span>
              <h2 className="section-title">Tudo o que um site local precisa, sem pacote surpresa e sem preocupação.</h2>
            </div>
            <div className="included-grid">
              {included.map((item) => (
                <div className="included-item" key={item}>
                  <span className="check" />
                  {item}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="section" id="faq">
          <div className="container">
            <div className="section-head center">
              <span className="section-label">Dúvidas frequentes</span>
              <h2 className="section-title">Perguntas que todo mundo faz.</h2>
            </div>
            <div className="faq-list">
              {faqs.map(([question, answer], index) => (
                <div className="faq-item" key={question}>
                  <button className="faq-question" onClick={() => setOpenFaq(openFaq === index ? -1 : index)}>
                    {question}
                    <span>{openFaq === index ? "-" : "+"}</span>
                  </button>
                  {openFaq === index && <div className="faq-answer">{answer}</div>}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="section checkout-section" id="checkout">
          <div className="container checkout-grid">
            <div>
              <span className="section-label">Começar agora</span>
              <h2 className="section-title">Seu site profissional pronto em até 48 horas</h2>
              <p className="section-copy">
                Preencha seus dados, escolha o plano e siga para o pagamento seguro. Depois da confirmação, você receberá o contato de um especialista que vai te acompanhar até estar satisfeito com o resultado.
              </p>
              <div className="chips">
                <span className="chip">Checkout seguro</span>
                <span className="chip">Garantia de satisfação</span>
                <span className="chip">Suporte humano</span>
              </div>
            </div>

            <form className="form-card" onSubmit={submitCheckout}>
              <div className="form-grid">
                <label className="form-field full">
                  <span>Plano</span>
                  <select
                    value={selectedPlan}
                    onChange={(event) => {
                      const plan = event.target.value as PlanKey;
                      setSelectedPlan(plan);
                      setForm((current) => ({ ...current, plan }));
                    }}
                  >
                    <option value="pro">Pro - R$197/mês</option>
                    <option value="lifetime">Lifetime - R$1.970 uma vez</option>
                  </select>
                </label>
                <label className="form-field">
                  <span>Seu nome *</span>
                  <input required value={form.name} onChange={(event) => setFormValue("name", event.target.value)} />
                </label>
                <label className="form-field">
                  <span>E-mail *</span>
                  <input required type="email" value={form.email} onChange={(event) => setFormValue("email", event.target.value)} />
                </label>
                <label className="form-field">
                  <span>WhatsApp *</span>
                  <input required inputMode="tel" value={form.whatsapp} onChange={(event) => setFormValue("whatsapp", event.target.value)} />
                </label>
                <label className="form-field">
                  <span>Endereço</span>
                  <input value={form.city} onChange={(event) => setFormValue("city", event.target.value)} />
                </label>
                <label className="form-field">
                  <span>Nome do negócio *</span>
                  <input required value={form.businessName} onChange={(event) => setFormValue("businessName", event.target.value)} />
                </label>
                <label className="form-field">
                  <span>Segmento</span>
                  <input value={form.segment} onChange={(event) => setFormValue("segment", event.target.value)} />
                </label>
                <label className="form-field">
                  <span>Possui domínio?</span>
                  <select value={form.hasDomain} onChange={(event) => setFormValue("hasDomain", event.target.value)}>
                    <option value="Não">Não</option>
                    <option value="Sim">Sim</option>
                  </select>
                </label>
                <label className="form-field">
                  <span>Possui logo?</span>
                  <select value={form.hasLogo} onChange={(event) => setFormValue("hasLogo", event.target.value)}>
                    <option value="Não">Não</option>
                    <option value="Sim">Sim</option>
                  </select>
                </label>
                <label className="form-field">
                  <span>Slogan</span>
                  <input value={form.slogan} onChange={(event) => setFormValue("slogan", event.target.value)} />
                </label>
                <label className="form-field">
                  <span>Domínio desejado</span>
                  <input value={form.desiredDomain} onChange={(event) => setFormValue("desiredDomain", event.target.value)} />
                </label>
                <label className="form-field">
                  <span>Cores de preferência</span>
                  <input
                    placeholder="Nome ou RGB"
                    value={form.preferredColors}
                    onChange={(event) => setFormValue("preferredColors", event.target.value)}
                  />
                </label>
                <label className="form-field">
                  <span>Estilo preferido</span>
                  <select value={form.preferredStyle} onChange={(event) => setFormValue("preferredStyle", event.target.value)}>
                    <option value="">Selecione</option>
                    <option value="Clean">Clean</option>
                    <option value="Futurista">Futurista</option>
                    <option value="Dark">Dark</option>
                    <option value="Moderno">Moderno</option>
                    <option value="Elegante">Elegante</option>
                  </select>
                </label>
                <label className="form-field full">
                  <span>Observações</span>
                  <textarea
                    placeholder="Conte tudo o que puder sobre seu negócio, serviços, público, preferências, etc. Quanto mais detalhes, melhor o resultado. Você poderá enviar mais infomrções e revisar com nosso especialista depois."
                    value={form.notes}
                    onChange={(event) => setFormValue("notes", event.target.value)}
                  />
                </label>
              </div>
              {error && <p className="form-error">{error}</p>}
              <button className="button button-primary" type="submit" disabled={submitting} style={{ width: "100%", marginTop: 16 }}>
                {submitting ? "Abrindo Stripe..." : `Ir para pagamento - ${offers[selectedPlan].price}`}
              </button>
              <p className="form-note">
                Ao continuar, você autoriza contato sobre a criação do site. O pagamento é processado fora deste site pela Stripe.
              </p>
            </form>
          </div>
        </section>
      </main>

      <Footer />
      <ConsentBanner />
    </div>
  );

  function setFormValue(key: keyof CheckoutForm, value: string) {
    setForm((current) => ({ ...current, [key]: value }));
  }
}

function DiagnosticLanding() {
  const [form, setForm] = useState<DiagnosticForm>({
    name: "",
    email: "",
    whatsapp: "",
    businessName: "",
    city: "",
    segment: "",
    instagram: "",
    googleMapsUrl: "",
    currentWebsite: "",
    mainGoal: "",
    notes: "",
    consent: false
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  function goForm() {
    document.querySelector("#diagnostico-form")?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  async function submitDiagnostic(event: FormEvent) {
    event.preventDefault();
    setSubmitting(true);
    setError("");

    try {
      const response = await fetch("/api/diagnostic-lead", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          sourcePath: window.location.pathname,
          project: "vitrine-website",
          attribution: getAttributionSnapshot()
        })
      });
      const payload = (await response.json().catch(() => ({}))) as { leadId?: string; resultUrl?: string; error?: string };
      if (!response.ok || !payload.leadId) throw new Error(payload.error || "Não foi possível solicitar o diagnóstico.");

      analytics.trackProductEvent("generate_lead", { form_name: "diagnostic" });

      storeDiagnosticSnapshot({
        ...form,
        leadId: payload.leadId,
        createdAt: new Date().toISOString()
      });

      window.location.href = payload.resultUrl || `/diagnostico/resultado/?leadId=${encodeURIComponent(payload.leadId)}`;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro ao solicitar diagnóstico.");
      setSubmitting(false);
    }
  }

  function setDiagnosticValue<K extends keyof DiagnosticForm>(key: K, value: DiagnosticForm[K]) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  return (
    <div className="site diagnostic-site">
      <DiagnosticHeader onCta={goForm} />
      <main>
        <section className="diagnostic-hero" id="topo">
          <div className="container diagnostic-hero-grid">
            <div>
              <span className="eyebrow">Diagnóstico gratuito · sem compromisso</span>
              <h1>Seu negócio está perdendo confiança antes do cliente chamar no WhatsApp?</h1>
              <p className="hero-copy">
                Receba uma análise prática da sua presença digital local. Vemos como seu negócio aparece no Google,
                Instagram, site, WhatsApp e informações públicas para apontar o que pode estar travando novos contatos.
              </p>
              <div className="hero-actions">
                <button className="button button-primary" onClick={goForm}>
                  Pedir diagnóstico gratuito
                </button>
                <a className="button button-secondary" href="#diagnostico-analise">
                  Ver o que analisamos
                </a>
              </div>
              <div className="chips">
                {["Google e Maps", "Instagram", "Site ou ausência de site", "WhatsApp", "Serviços e horários"].map((item) => (
                  <span className="chip" key={item}>
                    {item}
                  </span>
                ))}
              </div>
            </div>
            <DiagnosticReportMockup />
          </div>
        </section>

        <section className="diagnostic-trust">
          <div className="container diagnostic-trust-grid">
            <span>
              <CheckIcon /> Análise feita por especialista
            </span>
            <span>
              <CheckIcon /> Gratuito e sem compromisso
            </span>
            <span>
              <CheckIcon /> Linguagem simples
            </span>
          </div>
        </section>

        <section className="section section-cream">
          <div className="container">
            <div className="section-head">
              <span className="section-label">O problema</span>
              <h2 className="section-title">Antes de chamar, o cliente pesquisa.</h2>
              <p className="section-copy">
                Se encontra informações incompletas, link confuso, fotos desorganizadas ou nenhum site profissional,
                a confiança cai antes da primeira conversa.
              </p>
            </div>
            <div className="diagnostic-card-grid">
              {diagnosticProblems.map(([title, text], index) => (
                <article className="diagnostic-info-card" key={title}>
                  <span className="diagnostic-icon danger">{index + 1}</span>
                  <h3>{title}</h3>
                  <p>{text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="section">
          <div className="container">
            <div className="section-head center">
              <span className="section-label">A solução</span>
              <h2 className="section-title">Um olhar externo sobre sua presença digital.</h2>
              <p className="section-copy">
                O diagnóstico mostra como seu negócio aparece para alguém que ainda não te conhece e indica melhorias
                simples para deixar sua presença mais clara, confiável e pronta para gerar contato.
              </p>
            </div>
            <div className="diagnostic-note">
              Não é uma auditoria técnica avançada. É uma análise prática para pequenos negócios que querem entender
              onde melhorar sem virar especialistas em marketing ou tecnologia.
            </div>
          </div>
        </section>

        <section className="section section-dark" id="diagnostico-analise">
          <div className="container">
            <div className="section-head center">
              <span className="section-label">O que analisamos</span>
              <h2 className="section-title">O que entra no diagnóstico gratuito</h2>
            </div>
            <div className="diagnostic-card-grid diagnostic-analysis-grid">
              {diagnosticAnalyzed.map(([title, text], index) => (
                <article className="diagnostic-info-card dark" key={title}>
                  <span className="diagnostic-icon">{index + 1}</span>
                  <h3>{title}</h3>
                  <p>{text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="section">
          <div className="container diagnostic-delivery-grid">
            <div>
              <span className="section-label">A entrega</span>
              <h2 className="section-title">Você recebe um diagnóstico simples, direto e útil.</h2>
              <p className="section-copy">
                Depois da análise, enviamos um resumo com pontos fortes, problemas encontrados e recomendações práticas.
                Se fizer sentido, mostramos como a Vitrine Website pode ajudar.
              </p>
            </div>
            <div className="diagnostic-list-card">
              {diagnosticDeliverables.map((item) => (
                <div className="included-item" key={item}>
                  <span className="check" />
                  {item}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="section section-cream">
          <div className="container">
            <div className="section-head center">
              <span className="section-label">Como funciona</span>
              <h2 className="section-title">Simples, em 3 passos.</h2>
            </div>
            <div className="steps-grid">
              {diagnosticSteps.map(([num, title, text]) => (
                <div className="step" key={num}>
                  <span className="step-num">{num}</span>
                  <h3>{title}</h3>
                  <p>{text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="section diagnostic-form-section" id="diagnostico-form">
          <div className="container diagnostic-form-wrap">
            <div className="diagnostic-form-copy">
              <span className="section-label">Diagnóstico gratuito</span>
              <h2 className="section-title">Peça seu diagnóstico gratuito</h2>
              <p className="section-copy">
                Preencha as informações para avaliarmos como seu negócio aparece para clientes locais.
              </p>
              <div className="diagnostic-benefits">
                <span>
                  <CheckIcon /> Análise humana
                </span>
                <span>
                  <CheckIcon /> Sem compromisso
                </span>
                <span>
                  <CheckIcon /> Retorno com próximos passos
                </span>
              </div>
            </div>

            <form className="form-card diagnostic-form-card" onSubmit={submitDiagnostic}>
              <div className="form-grid">
                <label className="form-field">
                  <span>Seu nome *</span>
                  <input required value={form.name} onChange={(event) => setDiagnosticValue("name", event.target.value)} />
                </label>
                <label className="form-field">
                  <span>WhatsApp *</span>
                  <input required inputMode="tel" value={form.whatsapp} onChange={(event) => setDiagnosticValue("whatsapp", event.target.value)} />
                </label>
                <label className="form-field full">
                  <span>E-mail *</span>
                  <input required type="email" value={form.email} onChange={(event) => setDiagnosticValue("email", event.target.value)} />
                </label>
                <label className="form-field">
                  <span>Nome do negócio *</span>
                  <input required value={form.businessName} onChange={(event) => setDiagnosticValue("businessName", event.target.value)} />
                </label>
                <label className="form-field">
                  <span>Cidade / UF *</span>
                  <input required value={form.city} onChange={(event) => setDiagnosticValue("city", event.target.value)} />
                </label>
                <label className="form-field full">
                  <span>Segmento *</span>
                  <select required value={form.segment} onChange={(event) => setDiagnosticValue("segment", event.target.value)}>
                    <option value="">Selecione</option>
                    {diagnosticSegments.map((segment) => (
                      <option key={segment} value={segment}>
                        {segment}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="form-field full">
                  <span>Instagram do negócio</span>
                  <input placeholder="@seunegocio" value={form.instagram} onChange={(event) => setDiagnosticValue("instagram", event.target.value)} />
                </label>
                <label className="form-field full">
                  <span>Link do Google Maps</span>
                  <input value={form.googleMapsUrl} onChange={(event) => setDiagnosticValue("googleMapsUrl", event.target.value)} />
                </label>
                <label className="form-field full">
                  <span>Site atual, se tiver</span>
                  <input value={form.currentWebsite} onChange={(event) => setDiagnosticValue("currentWebsite", event.target.value)} />
                </label>
                <label className="form-field full">
                  <span>Principal objetivo</span>
                  <select value={form.mainGoal} onChange={(event) => setDiagnosticValue("mainGoal", event.target.value)}>
                    <option value="">Selecione</option>
                    {diagnosticGoals.map((goal) => (
                      <option key={goal} value={goal}>
                        {goal}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="form-field full">
                  <span>Observações</span>
                  <textarea
                    placeholder="Ex: tenho Instagram mas não tenho site; quero receber mais orçamentos pelo WhatsApp."
                    value={form.notes}
                    onChange={(event) => setDiagnosticValue("notes", event.target.value)}
                  />
                </label>
              </div>
              <label className="diagnostic-consent">
                <input
                  required
                  type="checkbox"
                  checked={form.consent}
                  onChange={(event) => setDiagnosticValue("consent", event.target.checked)}
                />
                <span>
                  Ao enviar, você autoriza contato sobre o diagnóstico e possíveis recomendações para melhorar a presença
                  digital do seu negócio.
                </span>
              </label>
              {error && <p className="form-error">{error}</p>}
              <button className="button button-primary" type="submit" disabled={submitting} style={{ width: "100%" }}>
                {submitting ? "Enviando..." : "Receber diagnóstico gratuito"}
              </button>
            </form>
          </div>
        </section>
      </main>
      <Footer showLinks={false} />
      <ConsentBanner />
      <button className="diagnostic-sticky" onClick={goForm}>
        Meu diagnóstico
      </button>
    </div>
  );
}

function DiagnosticResultPage() {
  const leadId = new URLSearchParams(window.location.search).get("leadId") || "";
  const snapshot = useMemo(() => readDiagnosticSnapshot(leadId), [leadId]);
  const fallbackResult = useMemo(() => buildDiagnosticResult(snapshot), [snapshot]);
  const [result, setResult] = useState<DiagnosticResultModel>(fallbackResult);
  const [resultState, setResultState] = useState<"loading" | "ready" | "fallback">(leadId ? "loading" : "fallback");

  useEffect(() => {
    let cancelled = false;
    setResult(fallbackResult);
    setResultState(leadId ? "loading" : "fallback");

    if (!leadId) return undefined;

    fetch(`/api/diagnostics/${encodeURIComponent(leadId)}`)
      .then(async (response) => {
        const payload = (await response.json().catch(() => ({}))) as DiagnosticApiPayload;
        if (!response.ok || !payload.diagnostic) throw new Error(payload.error || "Diagnóstico não encontrado.");
        return payload;
      })
      .then((payload) => {
        if (cancelled) return;
        setResult(mergeDiagnosticApiResult(payload, fallbackResult));
        setResultState("ready");
      })
      .catch(() => {
        if (cancelled) return;
        setResult(fallbackResult);
        setResultState("fallback");
      });

    return () => {
      cancelled = true;
    };
  }, [leadId, fallbackResult]);

  useEffect(() => {
    if (!leadId || resultState === "loading") return;

    const eventKey = `vitrine-diagnostic-result-view:${leadId}`;
    try {
      if (sessionStorage.getItem(eventKey)) return;
      sessionStorage.setItem(eventKey, "1");
    } catch {
      // Tracking should not depend on storage availability.
    }

    analytics.trackEvent("diagnostic_result_view", {
      form_name: "diagnostic",
      lead_id: leadId,
      diagnostic_state: resultState,
      diagnostic_score: result.score,
      diagnostic_rating: result.ratingLabel,
      diagnostic_is_live: result.isLive
    });
  }, [leadId, resultState, result.score, result.ratingLabel, result.isLive]);

  return (
    <div className="site diagnostic-result-site">
      <main className="diagnostic-result-page">
        <div className="diagnostic-result-shell">
          <div className="diagnostic-result-meta">
            <a className="brand" href="/">
              <BrandMark />
              <span className="brand-name">
                Vitrine<span>Website</span>
              </span>
            </a>
            <span>
              {resultState === "loading"
                ? "Consultando dados públicos"
                : result.isLive
                  ? "Diagnóstico com dados públicos"
                  : "Diagnóstico inicial"}{" "}
              · {result.date}
            </span>
          </div>

          <section className="diagnostic-result-hero">
            <div>
              <span className="section-label">Diagnóstico de presença digital</span>
              <h1>{result.businessName}</h1>
              <p>
                {result.segment} · {result.location}
              </p>
              <span className={`diagnostic-rating ${result.tone}`}>{result.ratingLabel}</span>
            </div>
            <ScoreGauge score={result.score} size="large" />
          </section>

          <section className="diagnostic-result-card">
            <span className="section-label">Resumo geral</span>
            <p>{result.summary}</p>
          </section>

          <section className="diagnostic-result-card">
            <h2>Blocos avaliados</h2>
            <p className="result-muted">Como sua presença se sai em cada frente que o cliente percebe.</p>
            <div className="diagnostic-bars">
              {result.blocks.map((block) => (
                <div className="diagnostic-bar-row" key={block.name}>
                  <div className="diagnostic-bar-head">
                    <div>
                      <strong>{block.name}</strong>
                      <span>{block.desc}</span>
                    </div>
                    <b style={{ color: block.color }}>{block.value}</b>
                  </div>
                  <div className="diagnostic-bar-track">
                    <span style={{ width: `${block.value}%`, background: block.color }} />
                  </div>
                </div>
              ))}
            </div>
          </section>

          <div className="diagnostic-result-columns">
            <section className="diagnostic-result-card success">
              <h2>Pontos fortes</h2>
              <ul>
                {result.strengths.map((item) => (
                  <li key={item}>
                    <CheckIcon /> {item}
                  </li>
                ))}
              </ul>
            </section>
            <section className="diagnostic-result-card warning">
              <h2>Pontos de atenção</h2>
              <ul>
                {result.attentions.map((item) => (
                  <li key={item}>
                    <CloseIcon /> {item}
                  </li>
                ))}
              </ul>
            </section>
          </div>

          <section className="diagnostic-result-card">
            <h2>Recomendações práticas</h2>
            <div className="diagnostic-recommendations">
              {result.recommendations.map((item, index) => (
                <div key={item}>
                  <span>{index + 1}</span>
                  <p>{item}</p>
                </div>
              ))}
            </div>
          </section>

          {result.sources.length > 0 && (
            <section className="diagnostic-result-card">
              <h2>Fontes consultadas</h2>
              <div className="diagnostic-source-list">
                {result.sources.map((source) => (
                  <span className={`diagnostic-source ${source.status}`} key={source.label}>
                    <strong>{source.label}</strong>
                    {source.value}
                  </span>
                ))}
              </div>
            </section>
          )}

          <section className="diagnostic-commercial">
            <span>Nossa recomendação</span>
            <h2>{result.commercialTitle}</h2>
            <p>{result.commercialText}</p>
            <div className="hero-actions">
              <a className="button button-primary" href="/">
                Quero vender melhor no WhatsApp
              </a>
              <a className="button button-secondary" href="/lifetime/">
                Prefiro pagamento único
              </a>
            </div>
          </section>

          <p className="diagnostic-disclaimer">
            Diagnóstico inicial e orientativo, baseado nas informações enviadas. Não representa relatório oficial do
            Google, análise de Analytics/Search Console, nem garantia de posição ou resultado de vendas.
          </p>
        </div>
      </main>
      <ConsentBanner />
    </div>
  );
}

function DiagnosticHeader({ onCta }: { onCta: () => void }) {
  return (
    <header className="header">
      <div className="container header-inner">
        <a className="brand" href="/diagnostico/">
          <BrandMark />
          <span className="brand-name">
            Vitrine<span>Website</span>
          </span>
        </a>
        <nav className="nav">
          <a href="#diagnostico-analise">O que analisamos</a>
          <button className="button button-primary" onClick={onCta}>
            Pedir diagnóstico
          </button>
        </nav>
      </div>
    </header>
  );
}

function DiagnosticReportMockup() {
  return (
    <div className="diagnostic-mock" aria-hidden="true">
      <div className="floating-badge diagnostic-found">
        <span className="badge-icon badge-icon-soft">
          <WhatsAppIcon />
        </span>
        <div>
          <span>WhatsApp</span>
          encontrado
        </div>
      </div>
      <div className="floating-badge diagnostic-alert">
        <span className="badge-icon badge-icon-warm">
          <BoltIcon />
        </span>
        <div>
          <span>Sem site</span>
          profissional
        </div>
      </div>
      <div className="diagnostic-report-card">
        <div className="diagnostic-report-top">
          <div>
            <span>Diagnóstico local</span>
            <strong>Presença Digital</strong>
            <small>Barbearia · Centro</small>
          </div>
          <ScoreGauge score={62} />
        </div>
        <div className="diagnostic-report-list">
          <span>
            <CheckIcon /> WhatsApp encontrado
          </span>
          <span>
            <CheckIcon /> Instagram ativo
          </span>
          <span className="warn">
            <CloseIcon /> Sem site profissional
          </span>
          <span className="warn">
            <CloseIcon /> Serviços pouco claros
          </span>
        </div>
      </div>
    </div>
  );
}

function ScoreGauge({ score, size = "default" }: { score: number; size?: "default" | "large" }) {
  const radius = size === "large" ? 63 : 40;
  const view = size === "large" ? 150 : 96;
  const stroke = size === "large" ? 13 : 9;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (circumference * Math.max(0, Math.min(100, score))) / 100;

  return (
    <div className={`score-gauge ${size === "large" ? "large" : ""}`}>
      <svg viewBox={`0 0 ${view} ${view}`}>
        <circle cx={view / 2} cy={view / 2} r={radius} />
        <circle
          cx={view / 2}
          cy={view / 2}
          r={radius}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeWidth={stroke}
        />
      </svg>
      <div>
        <strong>{score}</strong>
        <span>de 100</span>
      </div>
    </div>
  );
}

function storeDiagnosticSnapshot(snapshot: DiagnosticSnapshot) {
  try {
    localStorage.setItem(`vitrine-diagnostic:${snapshot.leadId}`, JSON.stringify(snapshot));
    localStorage.setItem("vitrine-diagnostic:last", JSON.stringify(snapshot));
  } catch {
    // Local storage is optional; the backend lead is already saved.
  }
}

function readDiagnosticSnapshot(leadId: string): DiagnosticSnapshot | null {
  try {
    const raw = localStorage.getItem(leadId ? `vitrine-diagnostic:${leadId}` : "vitrine-diagnostic:last");
    return raw ? (JSON.parse(raw) as DiagnosticSnapshot) : null;
  } catch {
    return null;
  }
}

function buildDiagnosticResult(snapshot: DiagnosticSnapshot | null): DiagnosticResultModel {
  const hasWebsite = Boolean(snapshot?.currentWebsite?.trim());
  const hasMaps = Boolean(snapshot?.googleMapsUrl?.trim());
  const hasInstagram = Boolean(snapshot?.instagram?.trim());
  const hasGoal = Boolean(snapshot?.mainGoal?.trim());
  const hasNotes = Boolean(snapshot?.notes?.trim());
  const score = Math.min(88, 38 + (hasInstagram ? 16 : 0) + (hasMaps ? 14 : 0) + (hasWebsite ? 18 : 0) + (hasGoal ? 7 : 0) + (hasNotes ? 5 : 0));
  const tone = score >= 72 ? "good" : score >= 52 ? "medium" : "low";

  const blocks = [
    {
      name: "Encontrabilidade",
      desc: "O cliente consegue encontrar o negócio?",
      value: hasMaps ? 72 : 48
    },
    {
      name: "Clareza",
      desc: "Serviços, horários, endereço e contatos fáceis de entender?",
      value: hasGoal || hasNotes ? 64 : 52
    },
    {
      name: "Confiança",
      desc: "A presença transmite profissionalismo?",
      value: hasWebsite ? 74 : 54
    },
    {
      name: "Conversão",
      desc: "Existe caminho claro para WhatsApp, rota ou orçamento?",
      value: hasMaps && hasInstagram ? 68 : 56
    },
    {
      name: "Estrutura digital",
      desc: "Existe uma página organizada centralizando informações?",
      value: hasWebsite ? 70 : 38
    }
  ].map((block) => ({ ...block, color: block.value >= 70 ? "#16a34a" : block.value >= 45 ? "#d98a16" : "#a34035" }));

  const strengths = [
    hasInstagram ? "Instagram informado para análise" : "",
    hasMaps ? "Link de Google Maps informado" : "",
    snapshot?.whatsapp ? "WhatsApp disponível para contato" : "",
    hasWebsite ? "Já existe um site ou link atual para revisar" : "",
    hasNotes ? "Você trouxe contexto sobre o que precisa melhorar" : ""
  ].filter(Boolean);

  const attentions = [
    !hasWebsite ? "Não há site profissional informado" : "",
    !hasMaps ? "Google Maps não foi informado para análise" : "",
    !hasGoal ? "Objetivo principal ainda precisa ficar mais claro" : "",
    "Serviços, horários, endereço e WhatsApp precisam aparecer juntos em uma página clara"
  ].filter(Boolean);

  return {
    date: new Date(snapshot?.createdAt || Date.now()).toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "long",
      year: "numeric"
    }),
    businessName: snapshot?.businessName || "Seu negócio",
    segment: snapshot?.segment || "Negócio local",
    location: snapshot?.city || "Brasil",
    score,
    tone,
    ratingLabel: score >= 72 ? "Boa presença digital" : score >= 52 ? "Presença com pontos a melhorar" : "Presença precisa de atenção",
    summary:
      snapshot
        ? `${snapshot.businessName} já tem sinais importantes de presença online, mas ainda precisa centralizar informações para facilitar a decisão de novos clientes. O diagnóstico completo deve olhar com cuidado para Google, Instagram, site, WhatsApp e clareza da oferta.`
        : "Este é um diagnóstico inicial ilustrativo. Para receber uma análise com dados do seu negócio, preencha o formulário da página de diagnóstico gratuito.",
    blocks,
    strengths: strengths.length ? strengths : ["WhatsApp disponível para contato", "Negócio local com potencial de presença digital"],
    attentions,
    recommendations: [
      "Centralizar serviços, fotos, endereço, horário, mapa e WhatsApp em uma página profissional.",
      "Organizar o link da bio para levar o cliente direto ao próximo passo.",
      "Manter nome, endereço e telefone consistentes entre Google, Instagram e site.",
      "Destacar os serviços principais e diferenciais antes do botão de contato.",
      "Usar uma página rápida no celular para reduzir dúvida e aumentar confiança."
    ],
    commercialTitle: "Transforme a primeira impressão do seu negócio em mais chamadas no WhatsApp.",
    commercialText:
      "Hoje o cliente decide se confia em você antes de mandar mensagem. Uma página profissional coloca serviços, fotos, endereço, horário, mapa e WhatsApp no lugar certo para reduzir dúvida, aumentar confiança e facilitar o próximo contato.",
    sources: ([
      hasMaps ? { label: "Google/Maps", value: "Link informado no formulário", status: "ok" as const } : null,
      hasWebsite ? { label: "Site atual", value: "URL informada no formulário", status: "ok" as const } : null,
      hasInstagram ? { label: "Instagram", value: "Informado para revisão manual", status: "ok" as const } : null
    ] as Array<DiagnosticResultSource | null>).filter((source): source is DiagnosticResultSource => Boolean(source)),
    isLive: false
  };
}

function mergeDiagnosticApiResult(payload: DiagnosticApiPayload, fallback: DiagnosticResultModel): DiagnosticResultModel {
  const diagnostic = payload.diagnostic;
  if (!diagnostic) return fallback;

  const score = clampResultScore(Number(diagnostic.score ?? fallback.score));
  const blocks = normalizeDiagnosticBlocks(diagnostic.blocks, fallback.blocks);
  const sources = normalizeDiagnosticSources(diagnostic.sources);
  const dateSource = diagnostic.updatedAt || diagnostic.createdAt;

  return {
    ...fallback,
    date: dateSource
      ? new Date(dateSource).toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" })
      : fallback.date,
    score,
    tone: diagnosticTone(score),
    ratingLabel: cleanText(diagnostic.ratingLabel) || fallback.ratingLabel,
    summary: cleanText(diagnostic.summary) || fallback.summary,
    commercialTitle: cleanText(diagnostic.commercialTitle) || fallback.commercialTitle,
    commercialText: cleanText(diagnostic.commercialText) || fallback.commercialText,
    strengths: normalizeStringList(diagnostic.strengths, fallback.strengths),
    attentions: normalizeStringList(diagnostic.attentions, fallback.attentions),
    recommendations: normalizeStringList(diagnostic.recommendations, fallback.recommendations),
    blocks,
    sources: sources.length ? sources : fallback.sources,
    isLive: true
  };
}

function normalizeStringList(value: unknown, fallback: string[]): string[] {
  if (!Array.isArray(value)) return fallback;
  const list = value.map((item) => cleanText(item)).filter(Boolean);
  return list.length ? list.slice(0, 8) : fallback;
}

function normalizeDiagnosticBlocks(value: unknown, fallback: DiagnosticResultBlock[]): DiagnosticResultBlock[] {
  if (!Array.isArray(value)) return fallback;
  const blocks = value
    .map((item) => {
      if (!item || typeof item !== "object") return null;
      const record = item as Record<string, unknown>;
      const name = cleanText(record.name);
      const desc = cleanText(record.desc);
      const blockValue = clampResultScore(Number(record.value));
      if (!name || !desc) return null;
      return {
        name,
        desc,
        value: blockValue,
        color: cleanText(record.color) || scoreColor(blockValue)
      };
    })
    .filter((block): block is DiagnosticResultBlock => Boolean(block));
  return blocks.length ? blocks : fallback;
}

function normalizeDiagnosticSources(value: unknown): DiagnosticResultSource[] {
  if (!value || typeof value !== "object") return [];
  const sources = value as Record<string, unknown>;
  const googlePlaces = readOptionalSourceRecord(sources.googlePlaces);
  const website = readOptionalSourceRecord(sources.website);
  const pageSpeed = readOptionalSourceRecord(sources.pageSpeed);
  const form = readSourceRecord(sources.form);

  return [
    googlePlaces
      ? {
          label: "Google/Maps",
          value:
            googlePlaces.status === "ok"
              ? googlePlaces.found
                ? `Perfil encontrado${readNestedText(googlePlaces.place, "name") ? `: ${readNestedText(googlePlaces.place, "name")}` : ""}`
                : "Busca feita, perfil não confirmado"
              : cleanText(googlePlaces.reason) || "Consulta não concluída",
          status: normalizeSourceStatus(googlePlaces.status)
        }
      : null,
    website
      ? {
          label: "Site atual",
          value:
            website.status === "ok"
              ? cleanText(website.url) || "Site carregado"
              : cleanText(website.reason) || "Sem site para analisar",
          status: normalizeSourceStatus(website.status)
        }
      : null,
    pageSpeed
      ? {
          label: "PageSpeed",
          value:
            pageSpeed.status === "ok" && pageSpeed.score !== null && pageSpeed.score !== undefined
              ? `Mobile ${pageSpeed.score}/100`
              : cleanText(pageSpeed.reason) || "Sem medição conclusiva",
          status: normalizeSourceStatus(pageSpeed.status)
        }
      : null,
    form.hasInstagram
      ? {
          label: "Instagram",
          value: "Informado para análise manual",
          status: "ok" as const
        }
      : null
  ].filter((source): source is DiagnosticResultSource => Boolean(source));
}

function readOptionalSourceRecord(value: unknown): Record<string, unknown> | null {
  const record = readSourceRecord(value);
  return Object.keys(record).length ? record : null;
}

function readSourceRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
}

function readNestedText(value: unknown, key: string): string {
  const record = readSourceRecord(value);
  return cleanText(record[key]);
}

function normalizeSourceStatus(value: unknown): DiagnosticResultSource["status"] {
  return value === "ok" || value === "failed" || value === "skipped" ? value : "skipped";
}

function cleanText(value: unknown): string {
  return String(value ?? "").trim();
}

function clampResultScore(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(100, Math.round(value)));
}

function diagnosticTone(score: number): DiagnosticResultModel["tone"] {
  if (score >= 72) return "good";
  if (score >= 52) return "medium";
  return "low";
}

function scoreColor(score: number): string {
  if (score >= 70) return "#16a34a";
  if (score >= 45) return "#d98a16";
  return "#a34035";
}

function Header({ focus, onCta }: { focus: Offer; onCta: () => void }) {
  return (
    <header className="header">
      <div className="container header-inner">
        <a className="brand" href={window.location.pathname}>
          <BrandMark />
          <span className="brand-name">
            Vitrine<span>Website</span>
          </span>
        </a>
        <nav className="nav">
          <a href="#como">Como funciona</a>
          <a href="#planos">Planos</a>
          <a href="#exemplos">Exemplos</a>
          <button className="button button-primary" onClick={onCta}>
            {focus.cta}
          </button>
        </nav>
      </div>
    </header>
  );
}

function SimpleHeader() {
  return (
    <header className="header">
      <div className="container header-inner">
        <a className="brand" href="/">
          <BrandMark />
          <span className="brand-name">
            Vitrine<span>Website</span>
          </span>
        </a>
        <nav className="nav">
          <a className="button button-primary" href="/">
            Voltar para o site
          </a>
        </nav>
      </div>
    </header>
  );
}

function SiteMockup() {
  return (
    <div className="mock-wrap phone-showcase" aria-hidden="true">
      <div className="floating-badge badge-top badge-seo">
        <span className="badge-icon badge-icon-soft">
          <TrendIcon />
        </span>
        <div>
          <span>SEO local</span>
          Aparece na busca
        </div>
      </div>
      <div className="floating-badge badge-bottom badge-performance">
        <span className="badge-icon badge-icon-warm">
          <BoltIcon />
        </span>
        <div>
          <span>Performance</span>
          Carrega rápido
        </div>
      </div>

      <div className="phone iphone-mock">
        <div className="phone-screen barber-screen">
          <div className="phone-status">
            <span>9:41</span>
            <span>5G</span>
          </div>
          <div className="barber-mobile-hero">
            <div className="barber-mobile-nav">
              <strong>Barbeiro Premium</strong>
              <span>Agendar</span>
            </div>
            <div className="barber-photo">
              <img src="/assets/barber-shop-premium.jpg" alt="" />
            </div>
            <span className="mock-kicker">Barbearia em São Paulo</span>
            <div className="mock-title">Corte afiado. Agenda pelo WhatsApp.</div>
            <div className="mock-wa">
              <WhatsAppIcon />
              Reservar horário
            </div>
          </div>
          <div className="mock-body">
            <div className="service-pill">
              <div>
                <small>Combo mais pedido</small>
                <strong>Corte + barba</strong>
              </div>
              <span>R$95</span>
            </div>
            <div className="mock-cards">
              <div className="mock-card">
                <small>Avaliação</small>
                <strong>4.9 estrelas</strong>
              </div>
              <div className="mock-card">
                <small>Hoje</small>
                <strong>Até 20h</strong>
              </div>
            </div>
            <div className="mock-map">
              Rua das Flores, 210
              <small>Ver rota no mapa</small>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function BrandMark() {
  return (
    <span className="brand-mark" aria-hidden="true">
      <StorefrontIcon />
    </span>
  );
}

function WhatsAppIcon({ className = "" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M5.4 19.4 6.3 16A7.2 7.2 0 1 1 9 18.4l-3.6 1Z"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.9"
      />
      <path
        d="M9.4 8.2c.2-.3.4-.4.7-.4h.5c.2 0 .4.1.5.3l.6 1.4c.1.2.1.4-.1.6l-.4.5c.5.9 1.2 1.6 2.1 2.1l.5-.4c.2-.2.4-.2.6-.1l1.4.6c.2.1.3.3.3.5v.5c0 .3-.1.5-.4.7-.5.3-1.1.4-1.7.2-2.5-.7-4.5-2.7-5.2-5.2-.2-.6-.1-1.2.2-1.7Z"
        fill="currentColor"
      />
    </svg>
  );
}

function StorefrontIcon() {
  return (
    <svg className="brand-icon" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M5 10.5V19h14v-8.5" />
      <path d="M4 10.5h16L18.8 5H5.2L4 10.5Z" />
      <path d="M4 10.5c0 1.2 1 2.1 2.2 2.1s2.2-.9 2.2-2.1c0 1.2 1 2.1 2.2 2.1s2.2-.9 2.2-2.1c0 1.2 1 2.1 2.2 2.1s2.2-.9 2.2-2.1c0 1.2 1 2.1 2.2 2.1" />
      <path d="M9 19v-4h6v4" />
    </svg>
  );
}

function TrendIcon() {
  return (
    <svg className="badge-svg" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M6 15.5 10 11.5 13 14.5 18.5 8.5" />
      <path d="M14.5 8.5h4v4" />
    </svg>
  );
}

function BoltIcon() {
  return (
    <svg className="badge-svg filled" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M13.4 2.8 5.8 13h5.2l-.8 8.2L18 10.6h-5.3l.7-7.8Z" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg className="forwho-icon-good" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12"></polyline>
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg className="forwho-icon-bad" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18"></line>
      <line x1="6" y1="6" x2="18" y2="18"></line>
    </svg>
  );
}

function NicheIcon({ kind }: { kind: NicheIconKey }) {
  if (kind === "barber") {
    return (
      <svg className="niche-icon" viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="6" cy="17" r="3" />
        <circle cx="18" cy="17" r="3" />
        <path d="M8.2 14.8 19 4" />
        <path d="M15.8 14.8 5 4" />
      </svg>
    );
  }
  if (kind === "salon") {
    return (
      <svg className="niche-icon" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M7 20 18.5 8.5 15.5 5.5 4 17v3h3Z" />
        <path d="M17 3v4" />
        <path d="M19 5h-4" />
        <path d="M20 14v3" />
        <path d="M21.5 15.5h-3" />
      </svg>
    );
  }
  if (kind === "pet") {
    return (
      <svg className="niche-icon filled" viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="6.5" cy="9" r="2" />
        <circle cx="11" cy="6.5" r="2" />
        <circle cx="15.5" cy="9" r="2" />
        <circle cx="18" cy="13.5" r="2" />
        <path d="M8.2 14.2c1.5-2.7 5.2-2.7 6.7 0 1.1 1.9-.2 4.1-2.4 4.1h-1.9c-2.2 0-3.5-2.2-2.4-4.1Z" />
      </svg>
    );
  }
  if (kind === "restaurant") {
    return (
      <svg className="niche-icon" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M7 3v18" />
        <path d="M4 3v6a3 3 0 0 0 6 0V3" />
        <path d="M16 3v18" />
        <path d="M16 3c3 1.8 4 4.2 3.6 7.4-.2 1.5-1.5 2.6-3.6 2.6" />
      </svg>
    );
  }
  if (kind === "clinic") {
    return (
      <svg className="niche-icon" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M12 5v14" />
        <path d="M5 12h14" />
        <rect x="4" y="4" width="16" height="16" rx="4" />
      </svg>
    );
  }
  if (kind === "repair") {
    return (
      <svg className="niche-icon" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M15.5 5.5a4.2 4.2 0 0 0 5 5L11 20l-4-4 9.5-9.5Z" />
        <path d="M7 16 4.5 18.5" />
      </svg>
    );
  }
  if (kind === "fight") {
    return (
      <svg className="niche-icon" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M6 11V7.5a2 2 0 0 1 4 0V11" />
        <path d="M10 10V6.5a2 2 0 0 1 4 0V11" />
        <path d="M14 10V7.5a2 2 0 0 1 4 0V13" />
        <path d="M6 11h9a4 4 0 0 1 0 8h-3a6 6 0 0 1-6-6v-2Z" />
      </svg>
    );
  }
  return (
    <svg className="niche-icon" viewBox="0 0 24 24" aria-hidden="true">
      <rect x="4" y="8" width="16" height="11" rx="2" />
      <path d="M9 8V6a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
      <path d="M4 13h16" />
    </svg>
  );
}

function PlanCard({ offer, featured, onChoose }: { offer: Offer; featured: boolean; onChoose: () => void }) {
  return (
    <article className={`plan ${featured ? "featured" : ""}`}>
      {featured && <span className="plan-ribbon">{offer.featuredLabel}</span>}
      <div className="plan-top">
        <h3 className="plan-name">{offer.name}</h3>
        <span className="plan-tag">{offer.tag}</span>
      </div>
      <div className="plan-price">
        {offer.price}
        <span className="plan-period">{offer.period}</span>
      </div>
      <p className="plan-desc">{offer.description}</p>
      <button className={`button ${featured ? "button-primary" : "button-secondary"}`} onClick={onChoose} style={{ width: "100%" }}>
        {offer.cta}
      </button>
      <ul>
        {offer.features.map((feature) => (
          <li className="check" key={feature}>
            {feature}
          </li>
        ))}
      </ul>
    </article>
  );
}

interface PolicySection {
  title: string;
  paragraphs?: string[];
  list?: string[];
  note?: { title: string; text: string };
}

const policyIntro = {
  updatedAt: "6 de julho de 2026",
  paragraphs: [
    "A Vitrine Website é um serviço da Caporal Studio criado para ajudar pequenos negócios locais a entender, organizar e melhorar sua presença digital.",
    "Nesta página explicamos, de forma clara, como funciona o serviço, quais são seus limites, quais dados coletamos, como usamos essas informações e quais são os direitos dos usuários.",
    "Ao usar nosso site, preencher formulários, solicitar um diagnóstico, contratar um plano ou entrar em contato conosco, você declara estar ciente destas políticas."
  ]
};

const policySections: PolicySection[] = [
  {
    title: "1. Sobre a Vitrine Website",
    paragraphs: [
      "A Vitrine Website oferece diagnóstico de presença digital, criação de sites profissionais para negócios locais e serviços relacionados à publicação, hospedagem, ajustes e acompanhamento básico da presença online.",
      "O serviço é voltado principalmente para pequenos negócios, prestadores de serviço e empresas locais que desejam ter uma presença digital mais clara, profissional e preparada para receber contatos pelo WhatsApp, telefone, formulário ou mapa."
    ]
  },
  {
    title: "2. Diagnóstico gratuito de presença digital",
    paragraphs: [
      "O diagnóstico gratuito é uma análise inicial e orientativa da presença digital do negócio.",
      "Ele pode considerar informações como:"
    ],
    list: [
      "nome do negócio;",
      "segmento;",
      "cidade e região de atuação;",
      "Instagram informado;",
      "site atual, se houver;",
      "link do Google Maps ou Perfil da Empresa, se informado;",
      "WhatsApp ou canais de contato enviados;",
      "informações públicas disponíveis online;",
      "dados preenchidos pelo próprio usuário no formulário."
    ],
    note: {
      title: "Importante",
      text: "Nesta fase, o diagnóstico não utiliza conexão direta com Google Meu Negócio, Google Analytics, Google Search Console ou contas privadas do cliente. Isso significa que o diagnóstico não acessa dados internos como impressões reais, cliques reais, consultas de busca, posição média, visitas do site, conversões ou relatórios privados do Google. O diagnóstico é baseado em informações fornecidas pelo usuário, dados públicos e análise orientativa. Ele não deve ser interpretado como auditoria técnica completa, relatório oficial do Google ou garantia de desempenho."
    }
  },
  {
    title: "3. Uso de inteligência artificial",
    paragraphs: [
      "A Vitrine Website pode usar ferramentas de inteligência artificial para apoiar a análise, organizar informações, sugerir melhorias, estruturar textos, gerar recomendações e acelerar a criação de páginas.",
      "As informações geradas com apoio de IA podem passar por revisão humana, especialmente em entregas comerciais, diagnósticos e criação de sites.",
      "A IA não deve ser interpretada como decisão automática final, garantia de resultado ou substituição integral da análise humana."
    ]
  },
  {
    title: "4. Planos e serviços pagos",
    paragraphs: [
      "A Vitrine Website pode oferecer diferentes planos, incluindo assinatura mensal e pagamento único.",
      "Plano Pro",
      "O plano Pro é um serviço mensal voltado para quem deseja site gerenciado, domínio próprio conforme regras do plano, hospedagem, suporte humano, ajustes mensais limitados, relatório e acompanhamento básico.",
      "O plano pode incluir:"
    ],
    list: [
      "site institucional de até 5 páginas;",
      "domínio próprio conforme regras comerciais do plano;",
      "hospedagem e SSL;",
      "botão de WhatsApp;",
      "mapa e endereço;",
      "formulário de contato;",
      "SEO técnico básico;",
      "SEO local básico;",
      "analytics configurado quando aplicável;",
      "relatório mensal;",
      "até 3 pequenos ajustes por mês;",
      "suporte em horário comercial."
    ]
  },
  {
    title: "Plano Lifetime",
    paragraphs: [
      "O plano Lifetime é um pagamento único para criação de um site institucional publicado em subdomínio gratuito da plataforma.",
      "O plano pode incluir:"
    ],
    list: [
      "site institucional de até 5 páginas;",
      "publicação em subdomínio gratuito da plataforma;",
      "hospedagem e SSL inclusos;",
      "WhatsApp, mapa e formulário;",
      "SEO técnico básico;",
      "1 a 2 rodadas de ajuste antes da publicação;",
      "30 dias de suporte após publicação."
    ]
  },
  {
    title: "O plano Lifetime não inclui, salvo contratação adicional",
    list: [
      "domínio próprio;",
      "ajustes mensais recorrentes;",
      "relatório mensal;",
      "suporte contínuo;",
      "gestão de anúncios;",
      "gestão completa de Google Meu Negócio;",
      "criação de e-commerce;",
      "sistema sob medida;",
      "área logada;",
      "funcionalidades complexas."
    ]
  },
  {
    title: "5. Prazos",
    paragraphs: [
      "A Vitrine Website pode informar prazos comerciais, como primeira versão em até 48 horas.",
      "Esse prazo começa a contar após o envio completo do briefing e das informações necessárias.",
      "O prazo pode ser impactado por fatores como:"
    ],
    list: [
      "briefing incompleto;",
      "ausência de imagens;",
      "falta de informações essenciais;",
      "necessidade de revisão adicional;",
      "demora na resposta do cliente;",
      "problemas técnicos externos;",
      "pendências de domínio, pagamento ou aprovação."
    ]
  },
  {
    title: "6. Ajustes e revisões",
    paragraphs: ["Os ajustes incluídos dependem do plano contratado.", "São considerados pequenos ajustes:"],
    list: [
      "alteração de textos;",
      "troca de imagens;",
      "correção de telefone, WhatsApp, endereço ou horário;",
      "ajuste de links;",
      "mudança de ordem de seções;",
      "pequenas alterações visuais;",
      "correções pontuais."
    ]
  },
  {
    title: "Não são considerados pequenos ajustes",
    paragraphs: undefined,
    list: [
      "redesign completo;",
      "mudança total da proposta visual;",
      "criação de páginas extras fora do escopo;",
      "criação de sistemas sob medida;",
      "e-commerce;",
      "área logada;",
      "integração complexa com sistemas externos;",
      "alterações ilimitadas;",
      "criação recorrente de campanhas ou peças de marketing."
    ],
    note: {
      title: "Fora do escopo",
      text: "Solicitações fora do escopo podem ser recusadas ou orçadas separadamente."
    }
  },
  {
    title: "7. Domínio, hospedagem e publicação",
    paragraphs: [
      "A Vitrine Website pode publicar sites em domínio próprio ou subdomínio gratuito da plataforma, conforme o plano contratado.",
      "No plano Lifetime, por padrão, o site é publicado em subdomínio gratuito da plataforma, como por exemplo nomedonegocio.vitrine.website.",
      "No plano Pro, o domínio próprio pode estar incluso conforme regras comerciais, disponibilidade, tipo de extensão e limites de valor definidos no momento da contratação.",
      "A troca de domínio, aquisição de novos domínios, configuração avançada de DNS ou uso de extensões específicas podem gerar custos adicionais.",
      "A hospedagem incluída está condicionada ao plano contratado, à manutenção do serviço, ao cumprimento destas políticas e à inexistência de uso indevido."
    ]
  },
  {
    title: "8. Limites e ausência de garantia de resultado",
    paragraphs: ["A Vitrine Website não promete:"],
    list: [
      "primeira posição no Google;",
      "aumento garantido de clientes;",
      "aumento garantido de vendas;",
      "número mínimo de acessos;",
      "número mínimo de mensagens no WhatsApp;",
      "desempenho garantido em anúncios;",
      "aprovação automática em plataformas de terceiros;",
      "resultado específico em SEO, Google Maps ou redes sociais."
    ],
    note: {
      title: "O que garantimos",
      text: "Oferecemos garantia de satisfação em até 7 dias após o pagamento: se, após a apresentação da primeira versão e o refinamento, você ainda não gostar do resultado, devolvemos o valor pago sem complicações. Fora essa garantia, o serviço tem como objetivo entregar estrutura, clareza, profissionalismo, organização, publicação e acompanhamento básico conforme o plano contratado. Resultados comerciais dependem de fatores externos, como localização, concorrência, demanda, reputação, avaliações, preço, qualidade do serviço, atendimento, investimento em mídia, sazonalidade e comportamento dos clientes."
    }
  },
  {
    title: "9. Pagamentos",
    paragraphs: [
      "Os pagamentos podem ser processados por provedores externos, como Stripe, Mercado Pago ou outras plataformas de pagamento.",
      "Ao realizar um pagamento, o usuário também estará sujeito aos termos, políticas e regras do provedor de pagamento utilizado.",
      "A Vitrine Website pode coletar e armazenar informações relacionadas à contratação, como plano escolhido, status do pagamento, data da compra, valor, dados de contato e identificadores da transação.",
      "Dados completos de cartão de crédito não são armazenados diretamente pela Vitrine Website quando o pagamento é processado por provedor externo."
    ]
  },
  {
    title: "10. Cancelamento, propriedade e reembolso",
    paragraphs: [
      "As regras de cancelamento dependem do plano contratado.",
      "No plano Pro, a assinatura pode ser cancelada a qualquer momento conforme as condições comerciais informadas no momento da contratação. Após o cancelamento, o site é removido do ar em até 30 dias, e podem ser interrompidos:"
    ],
    list: ["suporte;", "ajustes mensais;", "relatório;", "acompanhamento;", "hospedagem;", "uso de domínio gerenciado;", "acesso a recursos do plano."],
    note: {
      title: "Propriedade do site",
      text: "Cuidamos de toda a infraestrutura para garantir performance e segurança, mas o desenvolvimento entregue é de propriedade da Vitrine Website: não transferimos a posse do código-fonte, e o cliente não pode reutilizar a estrutura, os layouts ou o código para outros fins. No plano Lifetime, por se tratar de pagamento único, o suporte recorrente não está incluído após o período inicial de 30 dias, salvo contratação adicional. Condições específicas sobre reembolso, cancelamento, domínio, exportação de arquivos e manutenção serão informadas no momento da contratação ou nos termos comerciais aplicáveis."
    }
  }
];

const privacySections: PolicySection[] = [
  {
    title: "11. Quais dados coletamos",
    paragraphs: ["Podemos coletar dados fornecidos diretamente pelo usuário, como:"],
    list: [
      "nome;",
      "e-mail;",
      "telefone;",
      "WhatsApp;",
      "nome do negócio;",
      "segmento;",
      "cidade e estado;",
      "site atual;",
      "Instagram;",
      "link do Google Maps;",
      "informações sobre serviços oferecidos;",
      "imagens, logotipo e materiais enviados;",
      "preferências de estilo;",
      "informações do briefing;",
      "mensagens enviadas por formulários, WhatsApp ou e-mail."
    ]
  },
  {
    title: "Dados técnicos e de navegação",
    list: [
      "endereço IP;",
      "navegador;",
      "dispositivo;",
      "páginas acessadas;",
      "origem da visita;",
      "parâmetros UTM;",
      "cliques em botões;",
      "eventos de formulário;",
      "data e hora de acesso;",
      "cookies e identificadores similares."
    ]
  },
  {
    title: "12. Para que usamos os dados",
    list: [
      "responder solicitações;",
      "gerar diagnóstico gratuito;",
      "criar propostas;",
      "criar e publicar sites;",
      "entrar em contato por e-mail, WhatsApp ou telefone;",
      "prestar suporte;",
      "processar pagamentos;",
      "organizar briefings;",
      "melhorar nossos serviços;",
      "analisar desempenho de campanhas;",
      "medir conversões;",
      "enviar comunicações relacionadas ao serviço;",
      "cumprir obrigações legais, fiscais, regulatórias ou contratuais;",
      "prevenir fraude, abuso ou uso indevido."
    ]
  },
  {
    title: "13. Base legal para tratamento",
    paragraphs: [
      "Tratamos dados pessoais conforme hipóteses previstas na LGPD, incluindo, conforme o caso:"
    ],
    list: [
      "execução de contrato ou procedimentos preliminares relacionados a contrato;",
      "consentimento do titular;",
      "legítimo interesse;",
      "cumprimento de obrigação legal ou regulatória;",
      "exercício regular de direitos."
    ],
    note: {
      title: "Revogação do consentimento",
      text: "Quando o tratamento depender de consentimento, o usuário poderá revogá-lo, observadas as consequências da revogação sobre a prestação do serviço."
    }
  },
  {
    title: "14. Compartilhamento de dados",
    paragraphs: ["Podemos compartilhar dados com fornecedores necessários para operar o serviço, como:"],
    list: [
      "plataformas de pagamento;",
      "ferramentas de e-mail;",
      "ferramentas de automação;",
      "banco de dados ou CRM;",
      "Notion ou ferramentas similares de organização interna;",
      "serviços de hospedagem;",
      "provedores de analytics;",
      "plataformas de anúncios;",
      "ferramentas de inteligência artificial;",
      "serviços de armazenamento;",
      "prestadores técnicos e operacionais."
    ],
    note: {
      title: "Não vendemos dados",
      text: "O compartilhamento é feito na medida necessária para executar, medir, melhorar ou proteger o serviço. Não vendemos dados pessoais dos usuários."
    }
  },
  {
    title: "15. Notion, formulários e organização interna",
    paragraphs: [
      "Quando o usuário solicita diagnóstico, preenche formulário ou entra em contato, os dados podem ser enviados para uma base interna no Notion ou ferramenta semelhante.",
      "Essa base é usada para organizar leads, acompanhar solicitações, registrar status, gerar diagnósticos e dar continuidade ao atendimento."
    ]
  },
  {
    title: "16. Google Ads, tags e mensuração",
    paragraphs: [
      "Podemos usar ferramentas de publicidade e mensuração, como Google Ads, Google Tag Manager, Google Analytics ou tecnologias similares.",
      "Essas ferramentas podem registrar eventos como:"
    ],
    list: [
      "visualização de página;",
      "clique em botão;",
      "envio de formulário;",
      "acesso à página de resultado;",
      "origem da campanha;",
      "termo de busca;",
      "dispositivo;",
      "canal de aquisição."
    ],
    note: {
      title: "Por que medimos",
      text: "Essas informações ajudam a entender quais campanhas geram interesse e a melhorar a experiência do usuário."
    }
  },
  {
    title: "17. Cookies",
    paragraphs: ["Podemos usar cookies e tecnologias semelhantes para:"],
    list: ["funcionamento do site;", "segurança;", "análise de navegação;", "mensuração de campanhas;", "personalização;", "remarketing;", "melhoria da experiência."],
    note: {
      title: "Controle do usuário",
      text: "O usuário pode configurar o navegador para bloquear cookies ou apagar cookies existentes. Algumas funcionalidades podem ser afetadas. Quando aplicável, podemos exibir aviso ou ferramenta de consentimento para cookies."
    }
  },
  {
    title: "18. Dados de crianças e adolescentes",
    paragraphs: [
      "A Vitrine Website não é direcionada a crianças ou adolescentes.",
      "Não coletamos intencionalmente dados de menores de idade. Caso algum dado desse tipo seja identificado, poderemos removê-lo mediante solicitação."
    ]
  },
  {
    title: "19. Segurança",
    paragraphs: [
      "Adotamos medidas razoáveis para proteger os dados pessoais contra acesso não autorizado, perda, alteração, divulgação indevida ou uso inadequado.",
      "Nenhum sistema é absolutamente seguro. Por isso, também recomendamos que usuários evitem enviar informações sensíveis, documentos desnecessários, senhas, dados bancários ou informações que não sejam essenciais para o atendimento."
    ]
  },
  {
    title: "20. Retenção dos dados",
    paragraphs: ["Mantemos os dados pelo tempo necessário para:"],
    list: [
      "prestar o serviço;",
      "responder solicitações;",
      "manter histórico de atendimento;",
      "cumprir obrigações legais;",
      "resolver disputas;",
      "comprovar contratação;",
      "melhorar os serviços;",
      "prevenir fraude ou uso indevido."
    ],
    note: {
      title: "Depois disso",
      text: "Quando os dados não forem mais necessários, poderão ser excluídos, anonimizados ou mantidos conforme obrigação legal ou interesse legítimo aplicável."
    }
  },
  {
    title: "21. Direitos dos titulares",
    paragraphs: ["Nos termos da LGPD, o titular pode solicitar, conforme aplicável:"],
    list: [
      "confirmação da existência de tratamento;",
      "acesso aos dados;",
      "correção de dados incompletos, inexatos ou desatualizados;",
      "anonimização, bloqueio ou eliminação de dados desnecessários ou tratados em desconformidade;",
      "portabilidade, quando aplicável;",
      "informação sobre compartilhamento;",
      "revogação do consentimento;",
      "eliminação de dados tratados com base no consentimento;",
      "revisão de decisões automatizadas, quando aplicável."
    ],
    note: {
      title: "Como exercer",
      text: "Para exercer seus direitos, entre em contato pelo canal indicado abaixo."
    }
  },
  {
    title: "22. Canal de contato",
    paragraphs: [
      "Para dúvidas sobre estas políticas, privacidade, dados pessoais ou solicitações relacionadas à LGPD, entre em contato:"
    ],
    list: [
      "E-mail: hello@caporal.studio",
      "Responsável: Caporal Studio — a Vitrine Website é um produto da Caporal Studio (caporal.studio)",
      "CNPJ: 22.138.393/0001-37"
    ]
  },
  {
    title: "23. Atualizações desta página",
    paragraphs: [
      "Esta página pode ser atualizada periodicamente para refletir mudanças no serviço, nas ferramentas utilizadas, nas práticas de privacidade ou em exigências legais.",
      "A versão mais recente estará sempre disponível em https://vitrine.website/politicas."
    ]
  },
  {
    title: "24. Resumo em linguagem simples",
    paragraphs: [
      "A Vitrine Website coleta os dados que você envia para solicitar diagnóstico, contratar um plano ou criar seu site.",
      "Usamos esses dados para analisar sua presença digital, entrar em contato, criar propostas, desenvolver seu site, medir campanhas e prestar suporte.",
      "Não vendemos seus dados.",
      "O diagnóstico gratuito é inicial e orientativo. Ele não acessa dados privados do seu Google Meu Negócio, Analytics ou Search Console.",
      "A Vitrine Website não garante vendas, clientes, ranking no Google ou resultado comercial específico.",
      "Nosso objetivo é ajudar seu negócio a ter uma presença digital mais clara, profissional e preparada para receber clientes."
    ]
  }
];

function PolicySectionCard({ section }: { section: PolicySection }) {
  return (
    <section className="policy-card">
      <h2>{section.title}</h2>
      {section.paragraphs?.map((paragraph, index) => <p key={index}>{paragraph}</p>)}
      {section.list && (
        <ul>
          {section.list.map((item, index) => (
            <li key={index}>{item}</li>
          ))}
        </ul>
      )}
      {section.note && (
        <div className="policy-note">
          <strong>{section.note.title}</strong>
          <p>{section.note.text}</p>
        </div>
      )}
    </section>
  );
}

function PoliciesPage() {
  return (
    <div className="site">
      <SimpleHeader />
      <main className="policy-page">
        <div className="policy-shell">
          <div className="policy-hero">
            <span className="section-label">Políticas, privacidade e transparência</span>
            <h1 className="section-title">Como funciona o nosso serviço e como cuidamos dos seus dados</h1>
            <p className="section-copy">Última atualização: {policyIntro.updatedAt}</p>
            {policyIntro.paragraphs.map((paragraph, index) => (
              <p key={index}>{paragraph}</p>
            ))}
          </div>

          {policySections.map((section) => (
            <PolicySectionCard section={section} key={section.title} />
          ))}

          <div className="policy-divider">
            <span className="section-label">Política de privacidade</span>
          </div>

          {privacySections.map((section) => (
            <PolicySectionCard section={section} key={section.title} />
          ))}
        </div>
      </main>
      <Footer showLinks={false} />
      <ConsentBanner />
    </div>
  );
}

function CheckoutResult({ kind }: { kind: "success" | "cancel" }) {
  const success = kind === "success";
  return (
    <div className="site">
      <SimpleHeader />
      <main className="success-page">
        <div className="success-card">
          <h1>{success ? "Pagamento iniciado com sucesso" : "Checkout cancelado"}</h1>
          <p>
            {success
              ? "Se o pagamento foi confirmado pela Stripe, seguimos para o briefing do seu site. Você pode salvar esta página e aguardar nosso contato."
              : "Nenhuma cobrança foi concluída. Você pode voltar, escolher o plano e tentar novamente quando quiser."}
          </p>
          <a className="button button-primary" href="/#checkout">
            {success ? "Voltar para o site" : "Tentar novamente"}
          </a>
        </div>
      </main>
      <Footer showLinks={false} />
      <ConsentBanner />
    </div>
  );
}

function Footer({ showLinks = true }: { showLinks?: boolean }) {
  return (
    <footer className="footer">
      <div className="container footer-inner">
        <div className="footer-brand">
          <a className="brand" href="/">
            <BrandMark />
            <span className="brand-name" style={{ color: "#fff" }}>
              Vitrine<span>Website</span>
            </span>
          </a>
          <p className="footer-tagline">
            Foco em experiência digital, atendimento humano e entrega rápida para pequenos negócios
          </p>
        </div>
        <div className="footer-links">
          {showLinks && (
            <>
              <a href="#planos">Planos</a>
              <a href="#faq">Dúvidas</a>
            </>
          )}
          <a href="/politicas">Políticas e privacidade</a>
          <a
            href="#"
            onClick={(event) => {
              event.preventDefault();
              window.dispatchEvent(new Event("vw:reopen-consent"));
            }}
          >
            Gerenciar cookies
          </a>
        </div>
      </div>
      <div className="footer-bottom">
        <div className="container">© 2026 Vitrine Website · by <a href="https://caporal.studio" style={{ textDecoration: "none", color: "#fff", fontWeight: 600 }}>Caporal Studio</a></div>
      </div>
    </footer>
  );
}

function ConsentBanner() {
  const [visible, setVisible] = useState(() => analytics.getConsent() === null);

  useEffect(() => {
    function reopen() {
      analytics.resetConsent();
      setVisible(true);
    }
    window.addEventListener("vw:reopen-consent", reopen);
    return () => window.removeEventListener("vw:reopen-consent", reopen);
  }, []);

  if (!visible) return null;

  function accept() {
    analytics.grantConsent();
    setVisible(false);
  }

  function decline() {
    analytics.denyConsent();
    setVisible(false);
  }

  return (
    <div className="consent-banner" role="dialog" aria-live="polite">
      <p>
        Usamos cookies para entender como você navega e melhorar sua experiência. Veja detalhes em{" "}
        <a href="/politicas">políticas e privacidade</a>.
      </p>
      <div className="consent-banner-actions">
        <button className="button button-secondary" onClick={decline}>
          Recusar
        </button>
        <button className="button button-primary" onClick={accept}>
          Aceitar
        </button>
      </div>
    </div>
  );
}

function variantCopy(variant: PlanKey) {
  if (variant === "lifetime") {
    return {
      badge: "Plano Lifetime · pague uma vez",
      title: "Site profissional para seu negócio, com pagamento único e sem mensalidade.",
      subtitle:
        "Criamos e publicamos seu site com WhatsApp, mapa, SEO local e hospedagem no subdomínio da plataforma. Você paga uma única vez, aprova o resultado e ele fica no ar.",
      chips: ["Pagamento único", "Subdomínio grátis", "30 dias de suporte"],
      planTitle: "Resolva de uma vez: R$1.970 e pronto.",
      planSubtitle:
        "O Lifetime coloca seu site no ar em um subdomínio gratuito da plataforma, sem mensalidade. Se quiser domínio próprio, ajustes mensais, relatório e acompanhamento contínuo, o plano Pro continua disponível."
    };
  }

  return {
    badge: "Plano Pro · site gerenciado",
    title: "Seu negócio local com site profissional pronto para receber clientes pelo WhatsApp.",
    subtitle:
      "Criamos e publicamos seu site com domínio próprio, SEO local, analytics, suporte e relatório mensal. Você responde algumas perguntas, aprova e nós cuidamos de tudo.",
    chips: ["R$197/mês", "Domínio próprio", "Ajustes todo mês"],
    planTitle: "Seu site publicado, atualizado e acompanhado por R$197/mês.",
    planSubtitle:
      "O plano Pro é para quem quer domínio próprio, suporte humano, ajustes mensais, relatório e acompanhamento contínuo. Se preferir pagar uma única vez, o Lifetime também está disponível."
  };
}

const root = document.getElementById("root");
if (!root) throw new Error("Root element not found");
createRoot(root).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
